import React, { useEffect, useState, useMemo } from "react";
import { useAtom } from "jotai";
import {
  db,
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
} from "../../../firebaseConfig";
import {
  staffDataAtom,
  themeAtom,
  labelsAtom,
  listsAtom,
} from "../../../-Atoms";
import Modal from "@mui/material/Modal";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Paper from "@mui/material/Paper";
import CircularProgress from "@mui/material/CircularProgress";
import Chip from "@mui/material/Chip";
import Fade from "@mui/material/Fade";
import Backdrop from "@mui/material/Backdrop";
import { DateTime } from "luxon";
import isEqual from "lodash/isEqual";

// Helper function to get all changed paths between two objects
const getChangedPaths = (before, after, path = "") => {
  const changes = [];
  const allKeys = new Set([
    ...Object.keys(before || {}),
    ...Object.keys(after || {}),
  ]);

  allKeys.forEach((key) => {
    const currentPath = path ? `${path}.${key}` : key;
    const beforeValue = before?.[key];
    const afterValue = after?.[key];

    // Skip internal/system fields and obsolete tracking field
    if (["id", "lastModifiedBy", "lastModifiedAt", "tracking"].includes(key)) {
      return;
    }

    // Handle nested objects
    if (
      typeof beforeValue === "object" &&
      beforeValue !== null &&
      !Array.isArray(beforeValue) &&
      !(beforeValue instanceof Date) &&
      !beforeValue?.toDate && // Firebase Timestamp
      typeof afterValue === "object" &&
      afterValue !== null &&
      !Array.isArray(afterValue) &&
      !(afterValue instanceof Date) &&
      !afterValue?.toDate
    ) {
      changes.push(...getChangedPaths(beforeValue, afterValue, currentPath));
      return;
    }

    // Check if values are different
    if (!isEqual(beforeValue, afterValue)) {
      changes.push({
        path: currentPath,
        before: beforeValue,
        after: afterValue,
      });
    }
  });

  return changes;
};

// Helper to resolve label IDs to names
const resolveIdLabels = (idArray, labelsData) => {
  if (!Array.isArray(idArray) || idArray.length === 0) {
    return "No Labels";
  }

  const labelMap = new Map(labelsData.map(label => [label.id, label]));
  const labelNames = idArray
    .map(id => {
      const label = labelMap.get(id);
      return label ? label.name : id; // Fallback to ID if not found
    })
    .filter(Boolean);

  return labelNames.length > 0 ? labelNames.join(", ") : "No Labels";
};

// Helper to resolve list ID to name
const resolveIdList = (listId, listsData) => {
  if (!listId) return "No List";

  const list = listsData.find(l => l.id === listId);
  return list ? list.name : listId; // Fallback to ID if not found
};

// Helper to format field names for display
const formatFieldName = (path) => {
  const fieldNameMap = {
    jobName: "Job Name",
    jobNumber: "Job Number",
    client: "Client",
    studio: "Studio",
    bookedStart: "Start Time",
    bookedEnd: "End Time",
    confirmationStatus: "Confirmation Status",
    express: "Express",
    dueDate: "Due Date",
    expressDueDate: "Express Due Date",
    jobTypeFull: "Job Type",
    jobTypeShort: "Job Type Short",
    multipleDays: "Multi-Day Suffix",
    staff: "Staff",
    models: "Models",
    hmu: "Hair & Makeup",
    catering: "Catering",
    eventNotes: "Event Notes",
    shootSpecialRequirements: "Shoot Requirements",
    retouchingSpecialRequirements: "Retouching Requirements",
    invoicingSpecialRequirements: "Invoicing Requirements",
    imagesScheduledToBeShot: "Images Scheduled",
    imagesProcessed: "Images Processed",
    retoucher: "Retoucher",
    retouchingTime: "Retouching Time",
    tracking: "Tracking History",
    checklists: "Checklists",
    briefs: "Briefs",
    attachments: "Attachments",
    imageBreakdown: "Image Breakdown",
    outsourcers: "Outsourcing Jobs",
    isMultiDay: "Multi-Day Job",
    multiDayChildren: "Multi-Day Children",
    parentJobId: "Parent Job",
    multiDayOrder: "Multi-Day Order",
    multiDaySuffix: "Multi-Day Suffix",
    allDay: "All Day Event",
    title: "Title",
    start: "Start",
    end: "End",
    idLabels: "Labels",
    idLabelsPermanent: "Permanent Labels",
    idList: "List",
  };

  // Handle nested paths (e.g., "staff.shooter")
  const parts = path.split(".");
  const formatted = parts
    .map((part) => {
      // Use mapped name if available
      if (fieldNameMap[part]) return fieldNameMap[part];

      // Otherwise, convert camelCase to Title Case
      return part
        .replace(/([A-Z])/g, " $1")
        .replace(/^./, (str) => str.toUpperCase())
        .trim();
    })
    .join(" → ");

  return formatted;
};

// Helper to find differences in arrays of objects
const findArrayItemChanges = (beforeArray, afterArray, idKey = "id") => {
  const before = beforeArray || [];
  const after = afterArray || [];

  const changes = {
    added: [],
    removed: [],
    modified: [],
  };

  // Create maps for quick lookup
  const beforeMap = new Map(before.map((item) => [item[idKey], item]));
  const afterMap = new Map(after.map((item) => [item[idKey], item]));

  // Find added and modified items
  after.forEach((afterItem) => {
    const id = afterItem[idKey];
    const beforeItem = beforeMap.get(id);

    if (!beforeItem) {
      changes.added.push(afterItem);
    } else if (!isEqual(beforeItem, afterItem)) {
      changes.modified.push({ before: beforeItem, after: afterItem });
    }
  });

  // Find removed items
  before.forEach((beforeItem) => {
    const id = beforeItem[idKey];
    if (!afterMap.has(id)) {
      changes.removed.push(beforeItem);
    }
  });

  return changes;
};

// Custom formatters for complex array/object types
const formatChecklists = (beforeChecklists, afterChecklists, showChangeTags = false) => {
  const before = beforeChecklists || [];
  const after = afterChecklists || [];

  if (!Array.isArray(after) || after.length === 0) {
    return "No Checklists";
  }

  // If not showing change tags, show the "before" state (PREVIOUS panel)
  if (!showChangeTags) {
    // If there's no before state, show the after state without tags
    if (!Array.isArray(before) || before.length === 0) {
      return after
        .map((cl) => {
          const items = cl.checklistItems || [];
          const header = `${cl.name || "Untitled"}:`;

          if (items.length === 0) {
            return `${header}\n  Empty`;
          }

          const itemsList = items
            .map((item) => {
              const checkbox = item.checked ? "☑" : "☐";
              const title = item.title || "No Text";
              const flags = [];
              if (item.outsourcer) flags.push("[Outsourcer]");
              if (item.excluded) flags.push("[Excluded]");
              const flagText = flags.length > 0 ? ` ${flags.join(" ")}` : "";
              return `  ${checkbox} ${title}${flagText}`;
            })
            .join("\n");

          return `${header}\n${itemsList}`;
        })
        .join("\n\n");
    }

    // Show "before" state with [REMOVED] tags
    const afterMap = new Map(after.map(cl => [cl.id, cl]));

    return before
      .map((cl) => {
        const afterCl = afterMap.get(cl.id);
        const items = cl.checklistItems || [];
        const header = `${cl.name || "Untitled"}:`;

        if (items.length === 0) {
          return `${header}\n  Empty`;
        }

        // Map of items in the after state
        const afterItemsMap = afterCl
          ? new Map((afterCl.checklistItems || []).map(item => [item.id, item]))
          : new Map();

        const itemsList = items
          .map((item) => {
            const checkbox = item.checked ? "☑" : "☐";
            const title = item.title || "No Text";
            const flags = [];
            if (item.outsourcer) flags.push("[Outsourcer]");
            if (item.excluded) flags.push("[Excluded]");
            const flagText = flags.length > 0 ? ` ${flags.join(" ")}` : "";

            // Check if this item was removed
            const wasRemoved = !afterItemsMap.has(item.id);
            const removeTag = wasRemoved ? " [REMOVED]" : "";

            return `  ${checkbox} ${title}${flagText}${removeTag}`;
          })
          .join("\n");

        return `${header}\n${itemsList}`;
      })
      .join("\n\n");
  }

  // Showing change tags - include [ADDED], [CHECKED], etc.
  const results = [];
  const beforeMap = new Map(before.map(cl => [cl.id, cl]));

  after.forEach((afterCl) => {
    const beforeCl = beforeMap.get(afterCl.id);
    const items = afterCl.checklistItems || [];
    const header = `${afterCl.name || "Untitled"}:`;

    if (items.length === 0) {
      results.push(`${header}\n  Empty`);
      return;
    }

    if (!beforeCl) {
      // Entire checklist is new
      const itemsList = items
        .map((item) => {
          const checkbox = item.checked ? "☑" : "☐";
          const title = item.title || "No Text";
          const flags = [];
          if (item.outsourcer) flags.push("[Outsourcer]");
          if (item.excluded) flags.push("[Excluded]");
          const flagText = flags.length > 0 ? ` ${flags.join(" ")}` : "";
          return `  ${checkbox} ${title}${flagText} [ADDED]`;
        })
        .join("\n");
      results.push(`${header}\n${itemsList}`);
      return;
    }

    // Find changes in items
    const beforeItemsMap = new Map((beforeCl.checklistItems || []).map(item => [item.id, item]));
    const itemsList = items
      .map((item) => {
        const checkbox = item.checked ? "☑" : "☐";
        const title = item.title || "No Text";
        const flags = [];
        if (item.outsourcer) flags.push("[Outsourcer]");
        if (item.excluded) flags.push("[Excluded]");
        const flagText = flags.length > 0 ? ` ${flags.join(" ")}` : "";

        const beforeItem = beforeItemsMap.get(item.id);

        if (!beforeItem) {
          return `  ${checkbox} ${title}${flagText} [ADDED]`;
        }

        // Check what changed
        const changes = [];
        if (beforeItem.checked !== item.checked) {
          changes.push(item.checked ? "CHECKED" : "UNCHECKED");
        }
        if (beforeItem.title !== item.title) {
          changes.push("EDITED");
        }
        if (beforeItem.outsourcer !== item.outsourcer) {
          changes.push(item.outsourcer ? "OUTSOURCER" : "UNMARKED OUTSOURCER");
        }
        if (beforeItem.excluded !== item.excluded) {
          changes.push(item.excluded ? "EXCLUDED" : "UNMARKED EXCLUDED");
        }

        const changeTag = changes.length > 0 ? ` [${changes.join(", ")}]` : "";
        return `  ${checkbox} ${title}${flagText}${changeTag}`;
      })
      .join("\n");

    results.push(`${header}\n${itemsList}`);
  });

  return results.join("\n\n");
};

const formatCatering = (beforeCatering, afterCatering, showChangeTags = false) => {
  const before = beforeCatering || [];
  const after = afterCatering || [];

  if (!Array.isArray(after) || after.length === 0 && !Array.isArray(before)) {
    return "No Catering";
  }

  if (!showChangeTags) {
    // PREVIOUS panel - show with [REMOVED] tags
    if (!Array.isArray(before) || before.length === 0) {
      const items = after.map((item) => `• ${item.store || "Unknown"}: $${item.amount || "0.00"}`);
      const total = after.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0);
      if (total > 0) items.push(`\nTotal: $${total.toFixed(2)}`);
      return items.join("\n");
    }

    const afterMap = new Map(after.map(item => [item.id, item]));
    const items = before.map((item) => {
      const wasRemoved = !afterMap.has(item.id);
      const removeTag = wasRemoved ? " [REMOVED]" : "";
      return `• ${item.store || "Unknown"}: $${item.amount || "0.00"}${removeTag}`;
    });

    const total = before.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0);
    if (total > 0) {
      items.push(`\nTotal: $${total.toFixed(2)}`);
    }

    return items.join("\n");
  }

  // CURRENT panel - show with [ADDED]/[EDITED] tags
  const beforeMap = new Map(before.map(item => [item.id, item]));
  const items = after.map((item) => {
    const beforeItem = beforeMap.get(item.id);

    if (!beforeItem) {
      return `• ${item.store || "Unknown"}: $${item.amount || "0.00"} [ADDED]`;
    }

    const changes = [];
    if (beforeItem.store !== item.store) {
      changes.push("STORE CHANGED");
    }
    if (beforeItem.amount !== item.amount) {
      changes.push("AMOUNT CHANGED");
    }

    const changeTag = changes.length > 0 ? ` [${changes.join(", ")}]` : "";
    return `• ${item.store || "Unknown"}: $${item.amount || "0.00"}${changeTag}`;
  });

  const total = after.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0);
  if (total > 0) {
    items.push(`\nTotal: $${total.toFixed(2)}`);
  }

  return items.join("\n");
};

const formatModels = (beforeModels, afterModels, showChangeTags = false) => {
  const before = beforeModels || [];
  const after = afterModels || [];

  if (!Array.isArray(after) || after.length === 0 && !Array.isArray(before)) {
    return "No Models";
  }

  if (!showChangeTags) {
    // PREVIOUS panel
    if (!Array.isArray(before) || before.length === 0) {
      return after
        .map((model) => {
          const lines = [`• ${model.name || "Unnamed"}`];
          if (model.agency) lines.push(`  Agency: ${model.agency}`);
          if (model.callTime) {
            const time = DateTime.fromISO(model.callTime).toFormat("h:mm a");
            lines.push(`  Call Time: ${time}`);
          }
          if (model.insta) lines.push(`  Instagram: ${model.insta}`);
          return lines.join("\n");
        })
        .join("\n\n");
    }

    const afterMap = new Map(after.map(m => [m.id, m]));
    return before
      .map((model) => {
        const wasRemoved = !afterMap.has(model.id);
        const lines = [`• ${model.name || "Unnamed"}${wasRemoved ? " [REMOVED]" : ""}`];
        if (model.agency) lines.push(`  Agency: ${model.agency}`);
        if (model.callTime) {
          const time = DateTime.fromISO(model.callTime).toFormat("h:mm a");
          lines.push(`  Call Time: ${time}`);
        }
        if (model.insta) lines.push(`  Instagram: ${model.insta}`);
        return lines.join("\n");
      })
      .join("\n\n");
  }

  // CURRENT panel
  const beforeMap = new Map(before.map(m => [m.id, m]));
  return after
    .map((model) => {
      const beforeItem = beforeMap.get(model.id);
      const changes = [];

      if (!beforeItem) {
        changes.push("ADDED");
      } else {
        if (beforeItem.name !== model.name) changes.push("NAME");
        if (beforeItem.agency !== model.agency) changes.push("AGENCY");
        if (beforeItem.callTime !== model.callTime) changes.push("CALL TIME");
      }

      const changeTag = changes.length > 0 ? ` [${changes.join(", ")}]` : "";
      const lines = [`• ${model.name || "Unnamed"}${changeTag}`];
      if (model.agency) lines.push(`  Agency: ${model.agency}`);
      if (model.callTime) {
        const time = DateTime.fromISO(model.callTime).toFormat("h:mm a");
        lines.push(`  Call Time: ${time}`);
      }
      if (model.insta) lines.push(`  Instagram: ${model.insta}`);
      return lines.join("\n");
    })
    .join("\n\n");
};

const formatHMU = (beforeHMU, afterHMU, showChangeTags = false) => {
  const before = beforeHMU || [];
  const after = afterHMU || [];

  if (!Array.isArray(after) || after.length === 0 && !Array.isArray(before)) {
    return "No HMU";
  }

  if (!showChangeTags) {
    // PREVIOUS panel
    if (!Array.isArray(before) || before.length === 0) {
      return after
        .map((person) => {
          const lines = [`• ${person.name || "Unnamed"}`];
          if (person.agency) lines.push(`  Agency: ${person.agency}`);
          if (person.callTime) {
            const time = DateTime.fromISO(person.callTime).toFormat("h:mm a");
            lines.push(`  Call Time: ${time}`);
          }
          return lines.join("\n");
        })
        .join("\n\n");
    }

    const afterMap = new Map(after.map(h => [h.id, h]));
    return before
      .map((person) => {
        const wasRemoved = !afterMap.has(person.id);
        const lines = [`• ${person.name || "Unnamed"}${wasRemoved ? " [REMOVED]" : ""}`];
        if (person.agency) lines.push(`  Agency: ${person.agency}`);
        if (person.callTime) {
          const time = DateTime.fromISO(person.callTime).toFormat("h:mm a");
          lines.push(`  Call Time: ${time}`);
        }
        return lines.join("\n");
      })
      .join("\n\n");
  }

  // CURRENT panel
  const beforeMap = new Map(before.map(h => [h.id, h]));
  return after
    .map((person) => {
      const beforeItem = beforeMap.get(person.id);
      const changes = [];

      if (!beforeItem) {
        changes.push("ADDED");
      } else {
        if (beforeItem.name !== person.name) changes.push("NAME");
        if (beforeItem.agency !== person.agency) changes.push("AGENCY");
        if (beforeItem.callTime !== person.callTime) changes.push("CALL TIME");
      }

      const changeTag = changes.length > 0 ? ` [${changes.join(", ")}]` : "";
      const lines = [`• ${person.name || "Unnamed"}${changeTag}`];
      if (person.agency) lines.push(`  Agency: ${person.agency}`);
      if (person.callTime) {
        const time = DateTime.fromISO(person.callTime).toFormat("h:mm a");
        lines.push(`  Call Time: ${time}`);
      }
      return lines.join("\n");
    })
    .join("\n\n");
};

const formatImageBreakdown = (beforeBreakdown, afterBreakdown, showChangeTags = false) => {
  const before = beforeBreakdown || [];
  const after = afterBreakdown || [];

  if (!Array.isArray(after) || after.length === 0 && !Array.isArray(before)) {
    return "No Breakdown";
  }

  if (!showChangeTags) {
    // PREVIOUS panel
    if (!Array.isArray(before) || before.length === 0) {
      return after
        .map((item) => {
          const lines = [`• ${item.description || "No description"}`];
          if (item.quantityProcessed) lines.push(`  Processed: ${item.quantityProcessed}`);
          if (item.quantitySupplied) lines.push(`  Supplied: ${item.quantitySupplied}`);
          if (item.costPerFile) lines.push(`  Cost: $${item.costPerFile}`);
          return lines.join("\n");
        })
        .join("\n\n");
    }

    const afterMap = new Map(after.map(item => [item.id, item]));
    return before
      .map((item) => {
        const wasRemoved = !afterMap.has(item.id);
        const lines = [`• ${item.description || "No description"}${wasRemoved ? " [REMOVED]" : ""}`];
        if (item.quantityProcessed) lines.push(`  Processed: ${item.quantityProcessed}`);
        if (item.quantitySupplied) lines.push(`  Supplied: ${item.quantitySupplied}`);
        if (item.costPerFile) lines.push(`  Cost: $${item.costPerFile}`);
        return lines.join("\n");
      })
      .join("\n\n");
  }

  // CURRENT panel
  const beforeMap = new Map(before.map(item => [item.id, item]));
  return after
    .map((item) => {
      const beforeItem = beforeMap.get(item.id);
      const changes = [];

      if (!beforeItem) {
        changes.push("ADDED");
      } else {
        if (beforeItem.description !== item.description) changes.push("DESCRIPTION");
        if (beforeItem.quantityProcessed !== item.quantityProcessed) changes.push("PROCESSED");
        if (beforeItem.quantitySupplied !== item.quantitySupplied) changes.push("SUPPLIED");
        if (beforeItem.costPerFile !== item.costPerFile) changes.push("COST");
      }

      const changeTag = changes.length > 0 ? ` [${changes.join(", ")}]` : "";
      const lines = [`• ${item.description || "No description"}${changeTag}`];
      if (item.quantityProcessed) lines.push(`  Processed: ${item.quantityProcessed}`);
      if (item.quantitySupplied) lines.push(`  Supplied: ${item.quantitySupplied}`);
      if (item.costPerFile) lines.push(`  Cost: $${item.costPerFile}`);
      return lines.join("\n");
    })
    .join("\n\n");
};

const formatOutsourcers = (beforeOutsourcers, afterOutsourcers, showChangeTags = false) => {
  const before = beforeOutsourcers || [];
  const after = afterOutsourcers || [];

  if (!Array.isArray(after) || after.length === 0 && !Array.isArray(before)) {
    return "No Outsourcers";
  }

  if (!showChangeTags) {
    // PREVIOUS panel
    if (!Array.isArray(before) || before.length === 0) {
      return after
        .map((job) => {
          const lines = [`• ${job.outsourcer || "Unknown outsourcer"}`];
          if (job.dueDate) {
            const due = DateTime.fromISO(job.dueDate).toFormat("EEE d LLL yyyy");
            lines.push(`  Due: ${due}`);
          }
          if (job.staff) lines.push(`  Staff: ${job.staff}`);
          return lines.join("\n");
        })
        .join("\n\n");
    }

    return before
      .map((job, index) => {
        const afterJob = after[index];
        const wasRemoved = !afterJob;
        const lines = [`• ${job.outsourcer || "Unknown outsourcer"}${wasRemoved ? " [REMOVED]" : ""}`];
        if (job.dueDate) {
          const due = DateTime.fromISO(job.dueDate).toFormat("EEE d LLL yyyy");
          lines.push(`  Due: ${due}`);
        }
        if (job.staff) lines.push(`  Staff: ${job.staff}`);
        return lines.join("\n");
      })
      .join("\n\n");
  }

  // CURRENT panel
  return after
    .map((job, index) => {
      const beforeJob = before[index];
      const changes = [];

      if (!beforeJob) {
        changes.push("ADDED");
      } else {
        if (beforeJob.outsourcer !== job.outsourcer) changes.push("COMPANY");
        if (beforeJob.dueDate !== job.dueDate) changes.push("DUE DATE");
        if (beforeJob.staff !== job.staff) changes.push("STAFF");
      }

      const changeTag = changes.length > 0 ? ` [${changes.join(", ")}]` : "";
      const lines = [`• ${job.outsourcer || "Unknown outsourcer"}${changeTag}`];
      if (job.dueDate) {
        const due = DateTime.fromISO(job.dueDate).toFormat("EEE d LLL yyyy");
        lines.push(`  Due: ${due}`);
      }
      if (job.staff) lines.push(`  Staff: ${job.staff}`);
      return lines.join("\n");
    })
    .join("\n\n");
};

const formatStaff = (staff, staffData) => {
  if (!staff || typeof staff !== "object") {
    return "No Staff Assigned";
  }

  const staffMap = new Map();
  (staffData || []).forEach((s) => {
    if (s?.id) staffMap.set(s.id, s);
  });

  const roles = [];

  const roleLabels = {
    shooter: "Shooter",
    assistant: "Assistant",
    retoucher: "Retoucher",
    stylist: "Stylist",
  };

  Object.entries(staff).forEach(([role, staffId]) => {
    if (staffId && roleLabels[role]) {
      const staffMember = staffMap.get(staffId);
      const name = staffMember
        ? `${staffMember.firstName || ""} ${staffMember.lastName || ""}`.trim()
        : staffId;
      roles.push(`${roleLabels[role]}: ${name}`);
    }
  });

  return roles.length > 0 ? roles.join("\n") : "No Staff Assigned";
};

// Helper to format field values for display (now handles before/after comparison)
const formatFieldValue = (value, path = "", staffData = null, beforeValue = null, showChangeTags = false, labelsData = [], listsData = []) => {
  if (value === null || value === undefined) {
    return "Empty";
  }

  if (value === "") {
    return "Blank";
  }

  // Handle ID-based fields that need resolution
  if (path === "idLabels" || path === "idLabelsPermanent") {
    return resolveIdLabels(value, labelsData);
  }

  if (path === "idList") {
    return resolveIdList(value, listsData);
  }

  // Handle Firebase Timestamp
  if (value?.toDate && typeof value.toDate === "function") {
    return DateTime.fromJSDate(value.toDate())
      .setZone("Australia/Sydney")
      .toFormat("EEE d LLL yyyy • h:mm a");
  }

  // Handle Date objects
  if (value instanceof Date) {
    return DateTime.fromJSDate(value)
      .setZone("Australia/Sydney")
      .toFormat("EEE d LLL yyyy • h:mm a");
  }

  // Handle ISO date strings
  if (typeof value === "string" && value.match(/^\d{4}-\d{2}-\d{2}T/)) {
    const dt = DateTime.fromISO(value).setZone("Australia/Sydney");
    if (dt.isValid) {
      return dt.toFormat("EEE d LLL yyyy • h:mm a");
    }
  }

  // Handle arrays with custom formatters (pass both before and after)
  if (Array.isArray(value)) {
    if (value.length === 0) return "No Items";

    // Check if array contains objects
    const hasObjects = value.some(item => typeof item === "object" && item !== null);

    if (hasObjects) {
      // Use custom formatters for known array types - pass beforeValue and showChangeTags
      if (path === "checklists") return formatChecklists(beforeValue, value, showChangeTags);
      if (path === "catering") return formatCatering(beforeValue, value, showChangeTags);
      if (path === "models") return formatModels(beforeValue, value, showChangeTags);
      if (path === "hmu") return formatHMU(beforeValue, value, showChangeTags);
      if (path === "imageBreakdown") return formatImageBreakdown(beforeValue, value, showChangeTags);
      if (path === "outsourcers") return formatOutsourcers(beforeValue, value, showChangeTags);

      // Default to JSON for unknown arrays
      try {
        return JSON.stringify(value, null, 2);
      } catch (e) {
        return String(value);
      }
    }

    // Simple array - join with commas
    return value.map(item => String(item)).join(", ");
  }

  // Handle objects with custom formatters
  if (typeof value === "object") {
    // Use custom formatter for staff object
    if (path === "staff") {
      return formatStaff(value, staffData);
    }

    try {
      // Pretty print JSON
      return JSON.stringify(value, null, 2);
    } catch (e) {
      return "[Complex Object]";
    }
  }

  // Handle booleans
  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }

  return String(value);
};

// Helper to group versions by time window
const groupVersionsByTime = (versions) => {
  const TIME_WINDOW_MINUTES = 5;
  const grouped = [];

  for (let i = 0; i < versions.length; i++) {
    const current = versions[i];
    const group = {
      ...current,
      groupedVersions: [current],
      startTime: current.timestamp,
      endTime: current.timestamp,
    };

    // Look ahead for similar changes within time window
    while (i + 1 < versions.length) {
      const next = versions[i + 1];

      // Skip if no timestamp
      if (!current.timestamp || !next.timestamp) break;

      const timeDiff =
        Math.abs(current.timestamp.getTime() - next.timestamp.getTime()) /
        60000; // minutes

      // Group if within time window and same user
      if (timeDiff <= TIME_WINDOW_MINUTES && current.userId === next.userId) {
        group.groupedVersions.push(next);
        group.changes = [...group.changes, ...next.changes];
        group.endTime = next.timestamp;
        i++;
      } else {
        break;
      }
    }

    grouped.push(group);
  }

  return grouped;
};

// Timeline Entry Component
const TimelineEntry = ({ entry, isActive, onClick, getUserName, theme }) => {
  // Handle grouped time ranges
  const isGrouped =
    entry.groupedVersions && entry.groupedVersions.length > 1;

  let timestamp;
  if (!entry.timestamp) {
    timestamp = "Current";
  } else if (isGrouped && entry.startTime && entry.endTime) {
    const start = DateTime.fromJSDate(entry.startTime).toFormat("h:mm a");
    const end = DateTime.fromJSDate(entry.endTime).toFormat("h:mm a");
    const date = DateTime.fromJSDate(entry.startTime).toFormat("EEE d LLL");
    timestamp = `${date} • ${start}-${end}`;
  } else {
    timestamp = DateTime.fromJSDate(entry.timestamp).toFormat(
      "EEE d LLL • h:mm a"
    );
  }

  const userName = getUserName(entry.userId);
  const changeCount = entry.changes.length;

  return (
    <Paper
      elevation={isActive ? 3 : 0}
      onClick={onClick}
      sx={{
        p: 2,
        mb: 1.5,
        cursor: "pointer",
        border: isActive
          ? theme === "dark"
            ? "2px solid #64b5f6"
            : "2px solid #2196f3"
          : theme === "dark"
          ? "1px solid rgba(255, 255, 255, 0.12)"
          : "1px solid rgba(0, 0, 0, 0.12)",
        backgroundColor: isActive
          ? theme === "dark"
            ? "rgba(100, 181, 246, 0.15)"
            : "rgba(33, 150, 243, 0.1)"
          : theme === "dark"
          ? "rgba(255, 255, 255, 0.04)"
          : "rgba(0, 0, 0, 0.02)",
        transition: "all 0.2s ease",
        "&:hover": {
          backgroundColor:
            theme === "dark"
              ? "rgba(100, 181, 246, 0.08)"
              : "rgba(33, 150, 243, 0.05)",
          borderColor: theme === "dark" ? "#64b5f6" : "#2196f3",
        },
      }}
    >
      <Stack spacing={0.5}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: isActive ? 700 : 600,
            color: theme === "dark" ? "#e2e8f0" : "#1e293b",
          }}
        >
          {timestamp}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            color: theme === "dark" ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.6)",
          }}
        >
          {userName}
        </Typography>
        <Chip
          label={`${changeCount} field${changeCount !== 1 ? "s" : ""} changed`}
          size="small"
          sx={{
            height: 20,
            fontSize: "0.7rem",
            alignSelf: "flex-start",
          }}
        />
      </Stack>
    </Paper>
  );
};

// Field Change Component
const FieldChange = ({ change, theme, staffData, labels, lists }) => {
  const fieldName = formatFieldName(change.path);

  // Check if this is an array field that needs special diff handling
  const isArrayField = ["checklists", "catering", "models", "hmu", "imageBreakdown", "outsourcers"].includes(change.path);

  let beforeValue, afterValue;

  if (isArrayField) {
    // For arrays: show full content in both, but with [ADDED]/[CHANGED] tags in "current"
    beforeValue = formatFieldValue(change.before, change.path, staffData, null, false, labels, lists);
    afterValue = formatFieldValue(change.after, change.path, staffData, change.before, true, labels, lists);
  } else {
    // For regular fields: use normal formatting
    beforeValue = formatFieldValue(change.before, change.path, staffData, null, false, labels, lists);
    afterValue = formatFieldValue(change.after, change.path, staffData, null, false, labels, lists);
  }

  // Check if values are very long (for better display)
  const isLongValue = beforeValue.length > 500 || afterValue.length > 500;

  return (
    <Box
      sx={{
        p: 2,
        mb: 2,
        borderRadius: 1,
        border:
          theme === "dark"
            ? "1px solid rgba(255, 255, 255, 0.12)"
            : "1px solid rgba(0, 0, 0, 0.12)",
        backgroundColor:
          theme === "dark"
            ? "rgba(255, 255, 255, 0.02)"
            : "rgba(0, 0, 0, 0.01)",
      }}
    >
      <Typography
        variant="subtitle2"
        sx={{
          fontWeight: 700,
          mb: 1.5,
          color: theme === "dark" ? "#90caf9" : "#1976d2",
        }}
      >
        {fieldName}
      </Typography>

      <Stack spacing={1.5}>
        {/* Before value */}
        <Box>
          <Typography
            variant="caption"
            sx={{
              color: theme === "dark" ? "rgba(255, 255, 255, 0.5)" : "rgba(0, 0, 0, 0.5)",
              textTransform: "uppercase",
              letterSpacing: 0.5,
              fontSize: "0.65rem",
              mb: 0.5,
              display: "block",
            }}
          >
            Previous
          </Typography>
          <Paper
            elevation={0}
            sx={{
              p: 1.5,
              backgroundColor: theme === "dark" ? "rgba(239, 68, 68, 0.1)" : "rgba(239, 68, 68, 0.05)",
              border: theme === "dark" ? "1px solid rgba(239, 68, 68, 0.3)" : "1px solid rgba(239, 68, 68, 0.2)",
              maxHeight: isLongValue ? 300 : "auto",
              overflowY: isLongValue ? "auto" : "visible",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: theme === "dark" ? "#fca5a5" : "#dc2626",
                fontFamily: "monospace",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                fontSize: "0.75rem",
              }}
            >
              {beforeValue}
            </Typography>
          </Paper>
        </Box>

        {/* After value */}
        <Box>
          <Typography
            variant="caption"
            sx={{
              color: theme === "dark" ? "rgba(255, 255, 255, 0.5)" : "rgba(0, 0, 0, 0.5)",
              textTransform: "uppercase",
              letterSpacing: 0.5,
              fontSize: "0.65rem",
              mb: 0.5,
              display: "block",
            }}
          >
            Current
          </Typography>
          <Paper
            elevation={0}
            sx={{
              p: 1.5,
              backgroundColor: theme === "dark" ? "rgba(34, 197, 94, 0.1)" : "rgba(34, 197, 94, 0.05)",
              border: theme === "dark" ? "1px solid rgba(34, 197, 94, 0.3)" : "1px solid rgba(34, 197, 94, 0.2)",
              maxHeight: isLongValue ? 300 : "auto",
              overflowY: isLongValue ? "auto" : "visible",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: theme === "dark" ? "#86efac" : "#16a34a",
                fontFamily: "monospace",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                fontSize: "0.75rem",
              }}
            >
              {afterValue}
            </Typography>
          </Paper>
        </Box>
      </Stack>
    </Box>
  );
};

// Main History Dialog Component
const HistoryDialog = ({ open, onClose, jobId, jobData }) => {
  const [theme] = useAtom(themeAtom);
  const [staffData] = useAtom(staffDataAtom);
  const [labels] = useAtom(labelsAtom);
  const [lists] = useAtom(listsAtom);

  const [historyEntries, setHistoryEntries] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Load history from Firestore
  useEffect(() => {
    if (!open || !jobId) return;

    setLoadingHistory(true);

    const historyRef = collection(doc(db, "JobData", jobId), "history");
    const q = query(historyRef, orderBy("timestamp", "asc"));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const entries = snapshot.docs.map((historyDoc) => {
          const data = historyDoc.data();
          const timestamp =
            data.timestamp && data.timestamp.toDate
              ? data.timestamp.toDate()
              : null;
          return {
            id: historyDoc.id,
            ...data,
            timestamp,
          };
        });

        setHistoryEntries(entries);
        setLoadingHistory(false);
      },
      (error) => {
        console.error("Error loading job history:", error);
        setHistoryEntries([]);
        setLoadingHistory(false);
      }
    );

    return () => unsubscribe();
  }, [open, jobId]);

  // Build versions array with change diffs, grouping, and reverse order
  const versions = useMemo(() => {
    const results = historyEntries.map((entry) => {
      const changes = getChangedPaths(entry.before || {}, entry.after || {});
      return {
        id: entry.id,
        timestamp: entry.timestamp,
        userId: entry.userId,
        changes,
        after: entry.after,
      };
    });

    // Add current version
    if (jobData && Object.keys(jobData).length > 0) {
      const lastHistoryData =
        results.length > 0 ? results[results.length - 1].after : null;
      const changesFromLast = getChangedPaths(
        lastHistoryData || {},
        jobData || {}
      );

      results.push({
        id: "current",
        timestamp: null,
        userId: jobData.lastModifiedBy || null,
        changes: changesFromLast,
        after: jobData,
      });
    }

    // Group changes by time window
    const grouped = groupVersionsByTime(results);

    // Filter out versions with no changes
    const filtered = grouped.filter(version => version.changes.length > 0);

    // Reverse to show newest first
    return filtered.reverse();
  }, [historyEntries, jobData]);

  // Set initial selected index to 0 (newest, since reversed)
  useEffect(() => {
    if (!open) return;
    setSelectedIndex(0);
  }, [open, versions]);

  // Staff name lookup
  const staffMap = useMemo(() => {
    const map = new Map();
    (staffData || []).forEach((staff) => {
      if (!staff) return;
      [staff.id, staff.uid, staff.staffDocId, staff.authUserId].forEach(
        (key) => {
          if (key) map.set(key, staff);
        }
      );
    });
    return map;
  }, [staffData]);

  const getUserName = (userId) => {
    if (!userId) return "Unknown";

    const serviceAccountEmail =
      "firebase-adminsdk-d7dl4@its-connect-main.iam.gserviceaccount.com";

    if (
      typeof userId === "string" &&
      userId.toLowerCase() === serviceAccountEmail.toLowerCase()
    ) {
      return "Operator Shortcut";
    }

    const staff = staffMap.get(userId);
    if (staff) {
      return `${staff.firstName || ""} ${staff.lastName || ""}`.trim() || userId;
    }

    return userId;
  };

  const selectedVersion = versions[selectedIndex] || null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      closeAfterTransition
      disableAutoFocus
      disableRestoreFocus
      slots={{ backdrop: Backdrop }}
      slotProps={{
        backdrop: {
          timeout: 200,
          sx: {
            backgroundColor: "rgba(10, 15, 25, 0.7)",
          },
        },
      }}
    >
      <Fade in={open} timeout={200}>
        <Box
          sx={{
            outline: "none",
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: 2,
          }}
        >
          <Paper
            elevation={24}
            sx={{
              width: "95vw",
              maxWidth: "1400px",
              maxHeight: "90vh",
              borderRadius: 2,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              backgroundColor: theme === "dark" ? "#1e1e1e" : "#ffffff",
              position: "relative",
              zIndex: 1300,
            }}
          >
            {/* Header */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                p: 3,
                borderBottom:
                  theme === "dark"
                    ? "1px solid rgba(255, 255, 255, 0.12)"
                    : "1px solid rgba(0, 0, 0, 0.12)",
              }}
            >
              <Stack spacing={0.5}>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    color: theme === "dark" ? "#f8fafc" : "#0f172a",
                  }}
                >
                  Version History
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color:
                      theme === "dark"
                        ? "rgba(255, 255, 255, 0.6)"
                        : "rgba(0, 0, 0, 0.6)",
                  }}
                >
                  {jobData?.jobName || "Untitled Job"}
                </Typography>
              </Stack>
              <IconButton onClick={onClose} size="large">
                <CloseIcon />
              </IconButton>
            </Box>

            {/* Main Content */}
            {loadingHistory ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flex: 1,
                }}
              >
                <CircularProgress />
              </Box>
            ) : versions.length === 0 ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flex: 1,
                }}
              >
                <Typography
                  variant="body1"
                  sx={{
                    color:
                      theme === "dark"
                        ? "rgba(255, 255, 255, 0.5)"
                        : "rgba(0, 0, 0, 0.5)",
                  }}
                >
                  No history available for this job
                </Typography>
              </Box>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  flex: 1,
                  overflow: "hidden",
                }}
              >
                {/* Left Panel - Changes */}
                <Box
                  sx={{
                    flex: "0 0 70%",
                    p: 3,
                    overflowY: "auto",
                    borderRight:
                      theme === "dark"
                        ? "1px solid rgba(255, 255, 255, 0.12)"
                        : "1px solid rgba(0, 0, 0, 0.12)",
                  }}
                >
                  {selectedVersion && selectedVersion.changes.length > 0 ? (
                    <>
                      <Typography
                        variant="h6"
                        sx={{
                          mb: 3,
                          fontWeight: 600,
                          color: theme === "dark" ? "#e2e8f0" : "#1e293b",
                        }}
                      >
                        {selectedVersion.changes.length} Field
                        {selectedVersion.changes.length !== 1 ? "s" : ""} Changed
                      </Typography>
                      {selectedVersion.changes.map((change, index) => (
                        <FieldChange
                          key={`${change.path}-${index}`}
                          change={change}
                          theme={theme}
                          staffData={staffData}
                          labels={labels}
                          lists={lists}
                        />
                      ))}
                    </>
                  ) : (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        height: "100%",
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          color:
                            theme === "dark"
                              ? "rgba(255, 255, 255, 0.5)"
                              : "rgba(0, 0, 0, 0.5)",
                        }}
                      >
                        No changes in this version
                      </Typography>
                    </Box>
                  )}
                </Box>

                {/* Right Panel - Timeline */}
                <Box
                  sx={{
                    flex: "0 0 30%",
                    display: "flex",
                    flexDirection: "column",
                    overflowY: "auto",
                  }}
                >
                  {/* Timeline List */}
                  <Box sx={{ p: 2 }}>
                    <Stack spacing={0}>
                      {versions.map((entry, index) => (
                        <TimelineEntry
                          key={entry.id}
                          entry={entry}
                          isActive={index === selectedIndex}
                          onClick={() => setSelectedIndex(index)}
                          getUserName={getUserName}
                          theme={theme}
                        />
                      ))}
                    </Stack>
                  </Box>
                </Box>
              </Box>
            )}
          </Paper>
        </Box>
      </Fade>
    </Modal>
  );
};

export default HistoryDialog;
